import { useEffect, useState, useCallback } from "react";
import { listPrograms, listSessions, startEnrollmentCheckout } from "../apis/programsApi";
import { useNavigate, useLocation } from "react-router-dom";
import { formatDate } from "../utils/DateUtils";
import { Error } from "../components/admin/programs/Error";
import { isAdultAgeGroup } from "../utils/ageGroupUtils";
import {
  buildTimeSlotRanges,
  detectIncrementFromSlotCapacities,
  filterSlotsByAgeGroup,
  getAvailableAgeGroupsForSlots,
  getSessionSlotIncrementMinutes,
} from "../utils/programTimeSlotUtils";

const Programs = () => {
  const [programs, setPrograms] = useState<any[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<string>("");
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const preselectedProgramId =
    location.state?.programId || searchParams.get("programId") || "";
  const preselectedSessionId =
    location.state?.sessionId || searchParams.get("sessionId") || "";
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [dateOptions, setDateOptions] = useState<string[]>([]);
  const [allTimeSlotsForDate, setAllTimeSlotsForDate] = useState<string[]>([]);
  const [availableAgeGroups, setAvailableAgeGroups] = useState<string[]>([]);
  const [timeSlots, setTimeSlots] = useState<string[]>([]);

  const [form, setForm] = useState({
    email: "",
    participantName: "",
    participantAge: "",
    parentGuardianName: "",
    parentGuardianContact: "",
    emergencyContact: "",
    address: "",
    participantContact: "",
    medicalConditions: "",
    sessionId: "",
    selectedStartDate: "",
    selectedEndDate: "",
    selectedDate: "",
    selectedTimeSlot: "",
  });

  useEffect(() => {
    (async () => {
      const data = await listPrograms();
      const filteredData = data.filter((p: any) => p.isActive);
      setPrograms(filteredData);
      if (filteredData.length > 0) {
        const fromLink = filteredData.find((p: any) => p.id === preselectedProgramId);
        setSelectedProgramId(fromLink?.id || filteredData[0].id);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!selectedProgramId) return;
      const s = await listSessions(selectedProgramId);
      const filteredSessions = s.filter((sess: any) => sess.isActive);
      setSessions(filteredSessions);
      const match = filteredSessions.find((sess: any) => sess.id === preselectedSessionId);
      setForm((prev) => ({
        ...prev,
        sessionId: match ? match.id : "",
        participantAge: "",
        selectedDate: "",
        selectedTimeSlot: "",
      }));
    })();
  }, [selectedProgramId, preselectedSessionId]);

  // Helpers
  const pad = (n: number) => String(n).padStart(2, "0");
  const toISO = (d: Date) => {
    const localTime = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return localTime.toISOString().slice(0, 16);
  };
  const dateOnly = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const weekdayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"] as const;
  const addDays = (d: Date, days: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);

  useEffect(() => {
    const session = sessions.find((s) => s.id === form.sessionId);
    if (!session) {
      setDateOptions([]);
      setAllTimeSlotsForDate([]);
      setAvailableAgeGroups([]);
      setTimeSlots([]);
      return;
    }
    
    setForm(prev => ({ ...prev, participantAge: "", selectedDate: "", selectedTimeSlot: "", selectedStartDate: "" }));
    const holidays: Set<string> = new Set(Array.isArray(session.holidays) ? session.holidays : []);
    const dates: Set<string> = new Set();

    const includeDateIfValid = (dateStr: string) => {
      if (!holidays.has(dateStr)) dates.add(dateStr);
    };

    const startWindow = new Date(session.startDate);
    const endWindow = new Date(session.endDate);

    if (session.dayOfWeekDateRanges) {
      Object.keys(session.dayOfWeekDateRanges).forEach((day) => {
        const rng = session.dayOfWeekDateRanges[day];
        if (rng?.startDate) {
          includeDateIfValid(rng.startDate);
        }
      });
    } else {
      for (let d = new Date(startWindow); d <= endWindow; d = addDays(d, 1)) {
        includeDateIfValid(dateOnly(d));
      }
    }

    if (Array.isArray(session.extraDates)) {
      session.extraDates.forEach((ed: any) => {
        if (ed?.date) dates.add(ed.date);
      });
    }

    if (dates.size === 0) {
      dates.add(dateOnly(startWindow));
    }

    const sorted = Array.from(dates).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    setDateOptions(sorted);
    setAllTimeSlotsForDate([]);
    setAvailableAgeGroups([]);
    setTimeSlots([]);
  }, [form.sessionId, sessions]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    const isAdultParticipant = isAdultAgeGroup(form.participantAge);

    if (!form.email) newErrors.email = "Email is required";
    if (!form.participantName) newErrors.participantName = "Participant name is required";
    if (!form.participantAge) newErrors.participantAge = "Please select an age group";

    if (isAdultParticipant) {
      if (!form.participantContact) newErrors.participantContact = "Contact is required";
    } else {
      if (!form.parentGuardianName) newErrors.parentGuardianName = "Parent/Guardian name is required";
      if (!form.parentGuardianContact) newErrors.parentGuardianContact = "Parent/Guardian contact is required";
      if (!form.emergencyContact) newErrors.emergencyContact = "Emergency contact is required";
      if (!form.address) newErrors.address = "Home address is required";
    }
    if (!form.sessionId) newErrors.sessionId = "Please select a session";
    if (!form.selectedDate) newErrors.selectedDate = "Please select a date";
    if (!form.selectedTimeSlot) newErrors.selectedTimeSlot = "Please select a time slot";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onChange = useCallback((e: any) => {
    const { name, value } = e.target;
    if (name === "participantAge") {
      const becomingAdult = isAdultAgeGroup(value);
      const becomingMinor = !!value && !becomingAdult;
      setForm((prev) => ({
        ...prev,
        participantAge: value,
        ...(becomingAdult
          ? {
              parentGuardianName: "",
              parentGuardianContact: "",
              emergencyContact: "",
              address: "",
            }
          : {}),
        ...(becomingMinor ? { participantContact: "" } : {}),
      }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next.participantAge;
        if (becomingAdult) {
          delete next.parentGuardianName;
          delete next.parentGuardianContact;
          delete next.emergencyContact;
          delete next.address;
        }
        if (becomingMinor) {
          delete next.participantContact;
        }
        return next;
      });
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  }, [errors]);

  const onSubmit = async (e: any) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const selectedSession = sessions.find(s => s.id === form.sessionId);
      const url = await startEnrollmentCheckout(form.sessionId, {
        email: form.email,
        participantName: form.participantName,
        participantAge: form.participantAge,
        participantContact: form.participantContact,
        parentGuardianName: form.parentGuardianName,
        parentGuardianContact: form.parentGuardianContact,
        emergencyContact: form.emergencyContact,
        address: form.address,
        medicalConditions: form.medicalConditions,
        selectedStartDate: form.selectedStartDate,
        // Send the exact chosen slot; backend uses this for per-slot capacity
        timeSlot: form.selectedTimeSlot,
        isExistingUser: true, // For public enrollment, assume existing user
        totalAmount: selectedSession?.program?.price || 0,
      });
      window.location.href = url;
    } catch (err: any) {
      setErrors({ general: err.response.data.message || "" });
    } finally {
      setLoading(false);
    }
  };

  const buildTimeSlotsForDate = (session: any, dateStr: string): string[] => {
    const timesForDay = () => {
      const dayIdx = new Date(`${dateStr}T00:00:00`).getDay();
      const key = weekdayNames[dayIdx];
      return session.dayOfWeekTimes?.[key] || null;
    };
    const t = timesForDay();
    let startTime = t?.startTime;
    let endTime = t?.endTime;
    if (!startTime || !endTime) {
      const s = new Date(session.startDate);
      const e = new Date(session.endDate);
      startTime = `${pad(s.getHours())}:${pad(s.getMinutes())}`;
      endTime = `${pad(e.getHours())}:${pad(e.getMinutes())}`;
    }
    let incrementMinutes = getSessionSlotIncrementMinutes(session.sessionSlotType);
    if (incrementMinutes === null) {
      const dayIdx = new Date(`${dateStr}T00:00:00`).getDay();
      const dayKey = weekdayNames[dayIdx];
      incrementMinutes = detectIncrementFromSlotCapacities(session?.slotCapacities, dayKey);
    }

    if (incrementMinutes !== null) {
      return buildTimeSlotRanges(startTime, endTime, incrementMinutes);
    }

    return [`${startTime}-${endTime}`];
  };

  const InputField = useCallback(({ name, label, placeholder, value, type = "text", required = false, error = "", ...props }: any) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontWeight: 600, color: "#374151", fontSize: 14 }}>
        {label} {required && <span style={{ color: "#dc2626" }}>*</span>}
      </label>
      <input
        name={name}
        placeholder={placeholder}
        value={value}
        type={type}
        required={required}
        style={{
          padding: "14px 16px",
          border: `2px solid ${error ? "#ef4444" : "#d1d5db"}`,
          borderRadius: 10,
          fontSize: 15,
          backgroundColor: "#fff",
          transition: "all 0.2s",
          outline: "none",
        }}
        onFocus={(e) => {
          if (!error) e.target.style.borderColor = "#6b21a8";
        }}
        onBlur={(e) => {
          if (!error) e.target.style.borderColor = "#d1d5db";
        }}
        onChange={onChange}
        {...props}
      />
      {error && <span style={{ color: "#ef4444", fontSize: 13, marginTop: -2 }}>{error}</span>}
    </div>
  ), [onChange]);

  const SelectField = useCallback(({ name, value, placeholder, label, disabled = false, error = "", required = false, onCustomChange, children, ...props }: any) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontWeight: 600, color: "#374151", fontSize: 14 }}>
        {label} {required && <span style={{ color: "#dc2626" }}>*</span>}
      </label>
      <select
        name={name}
        value={value}
        disabled={disabled}
        style={{
          padding: "14px 16px",
          border: `2px solid ${error ? "#ef4444" : "#d1d5db"}`,
          borderRadius: 10,
          fontSize: 15,
          backgroundColor: disabled ? "#f3f4f6" : "#fff",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.7 : 1,
          transition: "all 0.2s",
          outline: "none",
        }}
        onFocus={(e) => {
          if (!error && !disabled) e.target.style.borderColor = "#6b21a8";
        }}
        onBlur={(e) => {
          if (!error) e.target.style.borderColor = "#d1d5db";
        }}
        onChange={onCustomChange || onChange}
        {...props}
      >
        <option value="">{placeholder}</option>
        {children}
      </select>
      {error && <span style={{ color: "#ef4444", fontSize: 13, marginTop: -2 }}>{error}</span>}
    </div>
  ), [onChange]);

  const getSessionName = () => {
    const session = sessions.find((s) => s.id === form.sessionId);
    return session?.name || "";
  };

  const getSelectedSessionSummary = () => {
    if (!form.selectedDate || !form.selectedTimeSlot) return null;
    const date = new Date(form.selectedDate + 'T00:00:00').toLocaleDateString([], { weekday: "long", year: "numeric", month: "long", day: "numeric" });
    return { date, session: getSessionName(), time: form.selectedTimeSlot, ageGroup: form.participantAge };
  };

  // Calculate progress
  const getProgress = () => {
    let completed = 0;
    const total = 4;
    if (selectedProgramId) completed++;
    if (form.sessionId) completed++;
    if (form.selectedDate && form.participantAge && form.selectedTimeSlot) completed++;
    const contactComplete = isAdultAgeGroup(form.participantAge)
      ? !!form.participantContact
      : !!(form.parentGuardianName && form.parentGuardianContact && form.emergencyContact && form.address);
    if (form.participantName && form.email && contactComplete) completed++;
    return (completed / total) * 100;
  };

  const summary = getSelectedSessionSummary();
  const isAdultParticipant = isAdultAgeGroup(form.participantAge);

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f8fafc", paddingBottom: 60 }}>
      {/* Hero Section */}
      {/* <div style={{ 
        background: "linear-gradient(135deg, #6b21a8 0%, #7c3aed 100%)", 
        color: "#fff", 
        padding: "60px 24px 40px",
        textAlign: "center"
      }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <h1 style={{ fontSize: 42, fontWeight: 800, margin: 0, marginBottom: 16, letterSpacing: "-0.5px" }}>
            Semi-Private Training
          </h1>
          <p style={{ fontSize: 19, opacity: 0.95, maxWidth: 700, margin: "0 auto 12px", lineHeight: 1.6 }}>
            Small Group Sessions focused on Hitting, Pitching & Catching Programs
          </p>
          <p style={{ fontSize: 16, opacity: 0.85, fontWeight: 500 }}>
            For ages 9 & Up
          </p>
        </div>
      </div> */}

      {/* Progress Bar */}
      <div style={{ 
        maxWidth: 1000, 
        margin: "20px auto 0", 
        padding: "0 24px"
      }}>
        <div style={{ 
          backgroundColor: "#fff", 
          borderRadius: 16, 
          padding: 20,
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>Registration Progress</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#6b21a8" }}>{Math.round(getProgress())}%</span>
          </div>
          <div style={{ 
            height: 8, 
            backgroundColor: "#e5e7eb", 
            borderRadius: 999,
            overflow: "hidden"
          }}>
            <div style={{ 
              height: "100%", 
              width: `${getProgress()}%`, 
              backgroundColor: "#6b21a8",
              transition: "width 0.5s ease",
              borderRadius: 999
            }} />
          </div>
        </div>
      </div>

      <div style={{ 
        maxWidth: 1000, 
        margin: "0 auto", 
        padding: "32px 24px",
        fontFamily: "system-ui, -apple-system, sans-serif"
      }}>
        <form onSubmit={onSubmit} style={{ display: "grid", gap: 24 }}>
          {/* Step 1: Program Selection */}
          <div style={{ 
            backgroundColor: "#fff", 
            padding: 28, 
            borderRadius: 16, 
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            border: "2px solid #e5e7eb"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div style={{ 
                width: 36, 
                height: 36, 
                borderRadius: "50%", 
                backgroundColor: "#6b21a8",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 16
              }}>1</div>
              <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: "#111827" }}>Choose Your Program</h2>
            </div>
            <SelectField
              name="program"
              value={selectedProgramId}
              placeholder="Select a program..."
              label="Program"
              required
              onCustomChange={(e: any) => setSelectedProgramId(e.target.value)}
            >
              {programs.map((p) => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </SelectField>
          </div>

          {/* Step 2: Session Selection */}
          {selectedProgramId && (
            <div style={{ 
              backgroundColor: "#fff", 
              padding: 28, 
              borderRadius: 16, 
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              border: "2px solid #e5e7eb"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                <div style={{ 
                  width: 36, 
                  height: 36, 
                  borderRadius: "50%", 
                  backgroundColor: "#6b21a8",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 16
                }}>2</div>
                <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: "#111827" }}>Select Session</h2>
              </div>
              <SelectField
                name="sessionId"
                value={form.sessionId}
                placeholder="Choose available session"
                label="Session"
                required
                error={errors.sessionId}
              >
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </SelectField>
            </div>
          )}

          {/* Step 3: Date & Time */}
          {form.sessionId && (
            <div style={{ 
              backgroundColor: "#fff", 
              padding: 28, 
              borderRadius: 16, 
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              border: "2px solid #e5e7eb"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                <div style={{ 
                  width: 36, 
                  height: 36, 
                  borderRadius: "50%", 
                  backgroundColor: "#6b21a8",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 16
                }}>3</div>
                <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: "#111827" }}>Pick Date & Time</h2>
              </div>
              
              <div style={{ display: "grid", gap: 20, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
                <SelectField
                  name="selectedDate"
                  value={form.selectedDate}
                  placeholder="Choose date"
                  label="Date"
                  required
                  disabled={!form.sessionId}
                  error={errors.selectedDate}
                  onCustomChange={(e: any) => {
                    const dateStr = e.target.value;
                    const session = sessions.find((s) => s.id === form.sessionId);
                    if (!session) return;

                    if (!dateStr) {
                      setAllTimeSlotsForDate([]);
                      setAvailableAgeGroups([]);
                      setTimeSlots([]);
                      setForm((prev) => ({
                        ...prev,
                        selectedDate: "",
                        selectedTimeSlot: "",
                        selectedStartDate: "",
                        selectedEndDate: "",
                        participantAge: "",
                      }));
                      return;
                    }

                    const slots = buildTimeSlotsForDate(session, dateStr);
                    setAllTimeSlotsForDate(slots);
                    setAvailableAgeGroups(getAvailableAgeGroupsForSlots(session, slots));
                    setTimeSlots([]);

                    let calculatedEndDate: Date;

                    // 1) Date-specific End Overrides take highest precedence
                    if (session.dateEndOverrides && session.dateEndOverrides[dateStr]) {
                      calculatedEndDate = new Date(session.dateEndOverrides[dateStr]);
                    } else if (session.dayOfWeekDateRanges) {
                      const dayIdx = new Date(dateStr).getDay();
                      const dayKey = weekdayNames[dayIdx];
                      const dayRange = session.dayOfWeekDateRanges[dayKey];

                      if (dayRange?.endDate) {
                        calculatedEndDate = new Date(dayRange.endDate);
                      } else {
                        calculatedEndDate = new Date(session.endDate);
                      }
                    } else {
                      calculatedEndDate = new Date(session.endDate);
                    }

                    const endIso = toISO(calculatedEndDate);
                    setForm((prev) => ({
                      ...prev,
                      selectedDate: dateStr,
                      selectedTimeSlot: "",
                      selectedStartDate: "",
                      selectedEndDate: endIso,
                      participantAge: "",
                    }));
                    if (errors.selectedDate) setErrors((prev) => ({ ...prev, selectedDate: "" }));
                  }}
                >
                  {dateOptions.map((d) => (
                    <option key={d} value={d}>
                      {formatDate(new Date(d + 'T00:00:00'))}
                    </option>
                  ))}
                </SelectField>

                <SelectField
                  name="participantAge"
                  value={form.participantAge}
                  placeholder={
                    !form.selectedDate
                      ? "Choose date first"
                      : availableAgeGroups.length === 0
                        ? "No age groups available"
                        : "Select age group"
                  }
                  label="Age Group"
                  required
                  disabled={!form.selectedDate || availableAgeGroups.length === 0}
                  error={errors.participantAge}
                  onCustomChange={(e: any) => {
                    const value = e.target.value;
                    const session = sessions.find((s) => s.id === form.sessionId);
                    const becomingAdult = isAdultAgeGroup(value);
                    const becomingMinor = !!value && !becomingAdult;
                    const filtered =
                      value && session
                        ? filterSlotsByAgeGroup(session, allTimeSlotsForDate, value)
                        : [];
                    setTimeSlots(filtered);
                    setForm((prev) => ({
                      ...prev,
                      participantAge: value,
                      selectedTimeSlot: "",
                      selectedStartDate: "",
                      ...(becomingAdult
                        ? {
                            parentGuardianName: "",
                            parentGuardianContact: "",
                            emergencyContact: "",
                            address: "",
                          }
                        : {}),
                      ...(becomingMinor ? { participantContact: "" } : {}),
                    }));
                    setErrors((prev) => {
                      const next = { ...prev };
                      delete next.participantAge;
                      delete next.selectedTimeSlot;
                      if (becomingAdult) {
                        delete next.parentGuardianName;
                        delete next.parentGuardianContact;
                        delete next.emergencyContact;
                        delete next.address;
                      }
                      if (becomingMinor) {
                        delete next.participantContact;
                      }
                      return next;
                    });
                  }}
                >
                  {availableAgeGroups.map((ageGroup) => (
                    <option key={ageGroup} value={ageGroup}>{ageGroup}</option>
                  ))}
                </SelectField>

                <SelectField
                  name="selectedTimeSlot"
                  value={form.selectedTimeSlot}
                  placeholder={!form.participantAge ? "Choose age group first" : "Choose time"}
                  label="Time Slot"
                  required
                  disabled={!form.participantAge || timeSlots.length === 0}
                  error={errors.selectedTimeSlot}
                  onCustomChange={(e: any) => {
                    const slot = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      selectedTimeSlot: slot,
                      selectedStartDate: form.selectedDate,
                    }));
                    if (errors.selectedTimeSlot) setErrors((prev) => ({ ...prev, selectedTimeSlot: "" }));
                  }}
                >
                  {timeSlots.map((t) => {
                    const session = sessions.find((s) => s.id === form.sessionId);
                    const cap = session?.slotCapacities?.[t];
                    const label = typeof cap === 'number' ? `${t}` : t;
                    return (
                      <option key={t} value={t}>{label}</option>
                    );
                  })}
                </SelectField>
              </div>

              {summary && (
                <div style={{
                  marginTop: 20,
                  padding: 20,
                  background: "linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)",
                  borderRadius: 12,
                  border: "2px solid #3b82f6"
                }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <div style={{ fontSize: 24 }}>📅</div>
                    <div>
                      <div style={{ fontWeight: 700, color: "#1e40af", marginBottom: 6, fontSize: 16 }}>Your Selection</div>
                      <div style={{ color: "#1e40af", lineHeight: 1.6 }}>
                        <strong>{summary.session}</strong><br />
                        {summary.date} at {summary.time}
                        {summary.ageGroup ? <><br />Age group: {summary.ageGroup}</> : null}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Participant Info */}
          {form.selectedDate && form.selectedTimeSlot && (
            <div style={{ 
              backgroundColor: "#fff", 
              padding: 28, 
              borderRadius: 16, 
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              border: "2px solid #e5e7eb"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                <div style={{ 
                  width: 36, 
                  height: 36, 
                  borderRadius: "50%", 
                  backgroundColor: "#6b21a8",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 16
                }}>4</div>
                <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: "#111827" }}>Participant Information</h2>
              </div>

              <div style={{ display: "grid", gap: 20 }}>
                <div style={{ display: "grid", gap: 20, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
                  <InputField
                    name="participantName"
                    label="Participant Name"
                    placeholder="Enter full name"
                    value={form.participantName}
                    required
                    error={errors.participantName}
                  />
                  <InputField
                    name="email"
                    label="Email Address"
                    placeholder="email@example.com"
                    value={form.email}
                    type="email"
                    required
                    error={errors.email}
                  />
                </div>

                <div style={{ display: "grid", gap: 20, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
                  {isAdultParticipant ? (
                    <InputField
                      name="participantContact"
                      label="Contact"
                      placeholder="(123) 456-7890"
                      value={form.participantContact}
                      type="tel"
                      required
                      error={errors.participantContact}
                    />
                  ) : (
                    <InputField
                      name="parentGuardianName"
                      label="Parent/Guardian Name"
                      placeholder="Enter parent/guardian name"
                      value={form.parentGuardianName}
                      required
                      error={errors.parentGuardianName}
                    />
                  )}
                </div>

                {!isAdultParticipant && (
                <>
                <div style={{ display: "grid", gap: 20, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
                  <InputField
                    name="parentGuardianContact"
                    label="Parent/Guardian Phone"
                    placeholder="(123) 456-7890"
                    value={form.parentGuardianContact}
                    type="tel"
                    required
                    error={errors.parentGuardianContact}
                  />
                  <InputField
                    name="emergencyContact"
                    label="Emergency Contact"
                    placeholder="Emergency phone number"
                    value={form.emergencyContact}
                    required
                    error={errors.emergencyContact}
                  />
                </div>

                <InputField 
                  name="address" 
                  label="Home Address"
                  placeholder="Enter home address" 
                  value={form.address} 
                  required 
                  error={errors.address} 
                />
                </>
                )}

                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontWeight: 600, color: "#374151", fontSize: 14 }}>
                    Medical Conditions (Optional)
                  </label>
                  <textarea
                    name="medicalConditions"
                    placeholder="Any allergies, conditions, or medications we should know about?"
                    value={form.medicalConditions}
                    rows={4}
                    style={{
                      padding: "14px 16px",
                      border: "2px solid #d1d5db",
                      borderRadius: 10,
                      fontSize: 15,
                      backgroundColor: "#fff",
                      resize: "vertical",
                      fontFamily: "inherit",
                      transition: "border-color 0.2s",
                      outline: "none"
                    }}
                    onFocus={(e) => { e.target.style.borderColor = "#6b21a8"; }}
                    onBlur={(e) => { e.target.style.borderColor = "#d1d5db"; }}
                    onChange={onChange}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          {form.selectedDate && form.selectedTimeSlot && (
            <div style={{ marginTop: 8 }}>
              {errors.general && (
                <div style={{
                  padding: 16,
                  backgroundColor: "#fef2f2",
                  border: "2px solid #fecaca",
                  borderRadius: 12,
                  color: "#dc2626",
                  marginBottom: 20,
                  fontSize: 15
                }}>
                  ⚠️ {errors.general}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "18px 32px",
                  background: loading ? "#9ca3af" : "linear-gradient(135deg, #6b21a8 0%, #7c3aed 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 12,
                  fontSize: 18,
                  fontWeight: 700,
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "all 0.3s",
                  boxShadow: loading ? "none" : "0 4px 6px -1px rgba(107, 33, 168, 0.3)",
                  transform: loading ? "none" : "translateY(0)",
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 8px 12px -2px rgba(107, 33, 168, 0.4)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(107, 33, 168, 0.3)";
                  }
                }}
              >
                {loading ? (
                  <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                    <span style={{ 
                      width: 20, 
                      height: 20, 
                      border: "3px solid #fff", 
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite"
                    }} />
                    Processing...
                  </span>
                ) : "Complete Registration & Pay"}
              </button>
            </div>
          )}
        </form>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Programs;